import { Anchor, Badge, Button, Group, Table, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { casIzMs } from '../oblika/oblika';
import { GlavaZaslona, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { Oznaka } from '../strani/PregledRacunov';
import { NovaPovratnaInformacija } from './NovaPovratnaInformacija';
import { sporocil, statusPovratne } from './povratne';

/**
 * Povratne informacije (novo v spletu; Swing jih samo pošilja): vse niti podjetja s stanjem, ki ga nastavlja naša
 * ekipa, najnovejša aktivnost najprej, strani na strežniku. Niti z neprebranim odgovorom so krepke z oznako »nov
 * odgovor«. Klik odpre pogovor, »Nova povratna informacija« pošlje novo in jo odpre.
 */
export function PregledPovratnihInformacij() {
  const seznam = useSeznam();
  const navigate = useNavigate();
  const [nova, setNova] = useState(false);
  const poizvedba = useQuery({
    queryKey: ['povratne', seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/povratne-informacije', {
          signal,
          params: { query: { stran: seznam.zaApi.stran, velikost: seznam.zaApi.velikost } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Povratne informacije"
        opis="Vaši predlogi, pohvale in težave ter naši odgovori. Vidijo jih vsi uporabniki podjetja."
        dejanja={
          <Button leftSection={<IconPlus size={18} />} onClick={() => setNova(true)}>
            Nova povratna informacija
          </Button>
        }
      />
      <Seznam
        seznam={seznam}
        glava={
          <>
            <Stolpec seznam={seznam} naziv="Sporočilo" />
            <Stolpec seznam={seznam} naziv="Stanje" />
            <Stolpec seznam={seznam} naziv="Poslal" />
            <Stolpec seznam={seznam} naziv="Zadnje sporočilo" />
          </>
        }
        vrstice={vrstice.map((p, i) => {
          const s = statusPovratne(p.status);
          return (
            <Table.Tr
              key={p.id}
              style={{ cursor: 'pointer', fontWeight: p.neprebrano ? 700 : undefined }}
              onClick={() => void navigate(`/povratne-informacije/${p.id}`)}
            >
              <Table.Td c="dimmed" className="brez-preloma">
                {seznam.zamik + i + 1}
              </Table.Td>
              <Table.Td maw={420}>
                <Group gap={6} wrap="nowrap">
                  <Anchor
                    component={Link}
                    to={`/povratne-informacije/${p.id}`}
                    onClick={(e) => e.stopPropagation()}
                    fw={p.neprebrano ? 700 : 500}
                    truncate
                    style={{ minWidth: 0 }}
                  >
                    {p.povzetek}
                  </Anchor>
                  {p.neprebrano && (
                    <Badge size="xs" color="red" variant="filled" style={{ flexShrink: 0 }}>
                      nov odgovor
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
              </Table.Td>
              <Table.Td className="brez-preloma">{p.avtor}</Table.Td>
              <Table.Td className="brez-preloma">
                {casIzMs(p.zadnjeSporocilo)}
                {(p.steviloSporocil ?? 0) > 1 && (
                  <Text span size="xs" c="dimmed">
                    {' '}
                    · {sporocil(p.steviloSporocil ?? 0)}
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
        stVrstic={vrstice.length}
        stStolpcev={4}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Še niste poslali povratne informacije. Veseli bomo vsakega predloga ali pripombe."
        napakaNaslov="Povratnih informacij ni bilo mogoče naložiti"
      />
      <NovaPovratnaInformacija
        odprto={nova}
        zapri={() => setNova(false)}
        poslano={(p) => void navigate(`/povratne-informacije/${p.id}`)}
      />
    </ZaslonSeznama>
  );
}
